import type { UkCustomsFileDto } from '@dhl/mdp-api'
import type { CustomsFile } from '~/modules/ppp/submodules/customs/model'

/**
 * Extracts the filename from a given path.
 * @param path The full path from which to extract the filename.
 * @returns The filename extracted from the path. If the path is empty or does not contain a filename, an empty string is returned.
 * @example
 * ```
 * getFilenameFromPath('/path/to/file.txt') // returns 'file.txt'
 * getFilenameFromPath('') // returns ''
 * getFilenameFromPath('/path/to/directory/') // returns ''
 * ```
 */
const getFilenameFromPath = (path: string): string => path.split('/').pop() ?? ''

/**
 * Converts a `UkCustomsFileDto` object to a `CustomsFile` object.
 * @param customsFile The `UkCustomsFileDto` object to be converted.
 * @returns A `CustomsFile` object containing the same properties as the input `customsFile`, with the addition of an `id` property set to the `blobPath` and a `filename` property extracted from the `blobPath`.
 * @example
 * ```
 * const dto: UkCustomsFileDto = {
 *   blobPath: 'sftp-outbound/ukmail-piece/2026/7/21/EUROPLUS-CUSTOMS-20260721100354_1.csv',
 *   uploaded: 1783364500783,
 * }
 * const customsFile = convertToCustomsFile(dto)
 * // customsFile will be:
 * {
 *   blobPath: 'sftp-outbound/ukmail-piece/2026/7/21/EUROPLUS-CUSTOMS-20260721100354_1.csv',
 *   uploaded: 1783364500783,
 *   id: 'sftp-outbound/ukmail-piece/2026/7/21/EUROPLUS-CUSTOMS-20260721100354_1.csv',
 *   filename: 'EUROPLUS-CUSTOMS-20260721100354_1.csv',
 * }
 * ```
 */
export const convertToCustomsFile = (customsFile: UkCustomsFileDto): CustomsFile => {
  return {
    ...customsFile,
    id: customsFile.blobPath,
    filename: getFilenameFromPath(customsFile.blobPath ?? ''),
  }
}
